import * as THREE from 'three';
import { glowTexture } from '../door/door-textures.js';
import { blankFace, canDraw } from './hud-materials.js';
import { CHART_FACE, CHART_REACH, HudChart, type ChartState } from './hud-chart.js';
import { octagonVertex } from './hud-parts.js';
import { unpickable } from './radial-menu.js';
import { HudBanner } from './hud-banner.js';
import { HudSparks } from './hud-sparks.js';
import { HudClock } from './hud-clock.js';
import { HudDock, type DockButton } from './hud-dock.js';
import { HeroMedallion, PORTRAIT_HEIGHT, PORTRAIT_WIDTH, type MedallionStance, type MedallionStances } from './hud-overlay.js';
import { HERO_ACTIONS } from '../../../shared/game/combat-types.js';
import { OrderQueueRow } from './order-queue-row.js';
import { BUTTON, ButtonKit } from './octagon-button.js';
import { warmScene } from '../scene-warmup.js';
import { RadialMenu, type RadialNode } from './radial-menu.js';

/** One hero's medallion as the HUD wants it drawn. */
export interface MedallionState {
  id: string; icon: string; tint: number;
  /** The address of the hero's painted portrait; without one the medallion shows `icon`. */
  portrait?: string;
  fallen?: boolean;
  /** Its player has left and the seat is held: the face dims. */
  away?: boolean;
  /** Down and Dying: the face cracks. */
  dying?: boolean;
  /** Health as a fraction of the maximum. */
  hp: number; actions: number; selected: boolean;
  /** Temporary hit points as a fraction of the maximum; absent is none. */
  ward?: number;
  /** Action crystals on the rail: two, three while the hero is steady. Absent is two. */
  slots?: number;
  wounds: number; maxWounds: number;
  /** The summons the hero has out and may keep (a Shadowmancer's Shadows); absent for heroes who summon nothing. */
  summons?: { count: number; limit: number };
  /** A Berserker's Rage: the Fury Dice held, their limit, and whether it Rages; absent for heroes who cannot Rage. */
  fury?: { dice: number[]; max: number; raging: boolean };
  /** An Oathsworn's Judgment Dice set aside and how many a roll brings; absent for heroes without Radiant Judgment. */
  judgment?: { dice: number[]; count: number };
  /** This player's hero: drawn full size. Other players' heroes are smaller. */
  own: boolean;
  /** Glyphs (icon URLs) of the orders the hero has queued, next first. */
  queue?: string[];
  /** The stances the hero holds, shown as notches on the frame's left side. Absent is none. */
  stances?: MedallionStances;
}
/** `chart` is the map chart: its face (`face`, with the spot under the pointer, `u` right and `v` down, 0–1) or one of its buttons. */
export type HudHit = { kind: 'medallion'; id: string } | { kind: 'stance'; id: string; stance: MedallionStance } | { kind: 'socket'; id: string } | { kind: 'dock'; id: string } | { kind: 'chart'; id: string; u?: number; v?: number };

/** The key light's and sky's colours by hour, mixed each frame the clock moves. */
const KEY_NIGHT = new THREE.Color(0x9db2d6), KEY_DAY = new THREE.Color(0xffe6b8), KEY_LOW = new THREE.Color(0xffb46e);
const SKY_NIGHT = new THREE.Color(0x5d6f8f), SKY_DAY = new THREE.Color(0xb7c6d8);
const POINT = new THREE.Vector2();

/** Below this opacity a transparent piece changes no pixel (a quarter of a colour step), so it is left out of the draw. */
const FAINT = 1 / 1024;

/** Pixel sizes on a 1080p screen; the whole layer scales with the interface size (`--ui-zoom`). */
const MEDALLION = 178, MARGIN = 26, DOCK_SIZE = 1.3;
const NO_STANCES: MedallionStances = { move: null, anticipating: false, hidden: false };
const NO_DICE: readonly number[] = [];

/**
 * The lit 3D layer of the in-world HUD, drawn after the world (on a canvas of its own over the page's panels): the party's bronze medallions down the left,
 * the selected hero's radial menu around them, the bottom bar's rail of buttons, the clock (the pause button) at the bottom right, and the
 * map chart on its compass rose at the top right, which unfolds to the middle of the screen. It knows nothing of the rules; it draws the state it is given and says what is
 * under a point. A warm lamp flickers above each piece, and a key light follows the in-game hour: warm and high by day, low and
 * moon-blue at night, when the lamps carry more of the light.
 */
export class Hud3dScene {
  readonly scene = new THREE.Scene();
  // The camera stands in front of every piece, so a picking ray starts before anything raised toward the viewer.
  readonly camera = (() => { const camera = new THREE.OrthographicCamera(0, 1, 1, 0, 1, 1000); camera.position.z = 500; return camera; })();
  private glow = canDraw() ? glowTexture() : null;
  /** The button parts, bronze and drawn glyph faces every rail, the menu, the chart's buttons and the order rows share. */
  private kit = new ButtonKit(this.glow);
  /** Each hero's medallion, and `slot`, where the column puts its centre; the medallion glides there (`update`). */
  private medallions = new Map<string, { medallion: HeroMedallion; face: string | THREE.Texture; own: boolean; slot: THREE.Vector2 }>();
  /** A medallion the player is dragging to a new place in the column, and the pointer's height from the top of the layer. */
  private dragged: { id: string; y: number } | null = null;
  private queues = new Map<string, OrderQueueRow>();
  /** The party's portrait pictures by address, loaded once and shared by every medallion that shows one. */
  private portraits = new Map<string, THREE.Texture>();
  private loader = new THREE.TextureLoader();
  private menu = new RadialMenu([], this.glow, this.kit);
  private dock = new HudDock(this.glow, this.kit);
  private docked = false;
  private dockLamp = new THREE.PointLight(0xffb469, 1.6, 0, 0);
  private clock = new HudClock(this.glow);
  private clockLamp = new THREE.PointLight(0xffb469, 1.6, 0, 0);
  /** The clock's own short rail beside it (Rest and Wait), lit by the clock's lamp. */
  private clockRail = new HudDock(this.glow, this.kit);
  private chart = new HudChart(this.glow, this.kit);
  /** The page's own menu buttons (settings, sound, developer tools) on a short rail in the top right corner. */
  private corner = new HudDock(this.glow, this.kit);
  private cornerLamp = new THREE.PointLight(0xffb469, 1.2, 0, 0);
  /** The combat status and Paused, on plaques at the top centre. */
  private combatBanner = new HudBanner('#ead9b8');
  private pausedBanner = new HudBanner('#e8c885', true);
  private chartLamp = new THREE.PointLight(0xffb469, 1.6, 0, 0);
  /** How far the chart has unfolded (0–1), easing toward `chartOpen`. */
  private unfold = 0; private chartOpen = false;
  private lamp = new THREE.PointLight(0xffb469, 2.4, 0, 0);
  private key = new THREE.DirectionalLight(0xffe2b0, 2);
  private sky = new THREE.HemisphereLight(0x8aa0bf, 0x1a120a, 1.1);
  /** How much the warm lamps carry, from the hour: more at night. */
  private lampStrength = 3;
  private menuLamp = new THREE.PointLight(0xffb469, 1.9, 0, 0);
  private raycaster = new THREE.Raycaster();
  private size = { width: 1, height: 1 };
  private anchor: { x: number; y: number } | null = null;
  private zoom = 1;
  private time = 0;
  private hovered: HudHit | null = null;
  private worldHovered: string | null = null;
  /** Sparks and motes thrown by the pieces: a struck medallion, a spent crystal, the clock when a fight begins. */
  private sparks = new HudSparks(this.glow);
  /** What each medallion last showed, so a change can be marked (a hit, a spent crystal), and the marks running on it. */
  private marks = new Map<string, MedallionMarks>();
  private crackTexture = canDraw() ? crackTexture() : null;
  private fighting = false;
  /** Each piece's solid box in its own frame, kept until its key (what changes its shape) changes. */
  private boxes = new Map<THREE.Object3D, { key: string; box: THREE.Box3 }>();
  /** The corner rail's lower edge on screen, which the chart hangs below. */
  private cornerBottom = 0;
  private faint: THREE.Object3D[] = [];


  constructor() {
    this.menu.visible = false;
    // A raking key from the upper left and close to the plane, so bevels, rims and crystals throw light and shade.
    this.key.position.set(-.85, .62, .42); this.scene.add(this.key, this.key.target);
    this.dock.visible = false; this.clock.visible = false; this.chart.visible = false;
    this.corner.visible = false; this.clockRail.visible = false;
    this.scene.add(this.sparks, this.corner, this.cornerLamp, this.combatBanner, this.pausedBanner, this.chart, this.chartLamp, this.menu, this.dock, this.dockLamp, this.clock, this.clockLamp, this.clockRail, this.lamp, this.menuLamp, this.sky, new THREE.AmbientLight(0x30261c, .5));
    this.setClock(720);
  }

  /** The column's places, top first: each hero, whether it is this player's, and its slot's centre height from the top of the layer. */
  medallionSpots(): { id: string; own: boolean; y: number }[] {
    return [...this.medallions].map(([id, entry]) => ({ id, own: entry.own, y: this.size.height - entry.slot.y })).sort((a, b) => a.y - b.y);
  }
  /**
   * Lifts a medallion out of the column to follow the pointer (`y`, from the top of the layer) while it is dragged; the others glide
   * apart round the gap it would drop into (the column's order, given by `setParty`). Null lets it glide back into its place.
   */
  setDragged(drag: { id: string; y: number } | null) { this.dragged = drag; }
  /** The interface size (`--ui-zoom`), so the layer matches the page's panels around it. */
  setZoom(zoom: number) { this.zoom = zoom; }
  /** Room kept free at the left edge, in CSS pixels, for a page's own panel (the scenario viewer's). */
  setInset(left: number) { this.inset = left; }
  private inset = 0;

  setParty(states: MedallionState[]) {
    const faces = new Map(states.map(state => [state.id, state.portrait && canDraw() ? this.portrait(state.portrait) : state.icon]));
    for (const [id, entry] of this.medallions) if (faces.get(id) !== entry.face) { entry.medallion.removeFromParent(); entry.medallion.dispose(); this.medallions.delete(id); }
    for (const [url, texture] of this.portraits) if (!states.some(state => state.portrait === url)) { texture.dispose(); this.portraits.delete(url); }
    // Your heroes stand at the bottom of the column, others above them. A column taller than most of the screen shrinks to fit.
    const needed = states.reduce((sum, state) => sum + (MEDALLION * (state.own ? 1 : .62) + 2) * this.zoom, MARGIN * this.zoom);
    const fit = Math.min(1, this.size.height * .78 / needed);
    let y = MARGIN * this.zoom, left: number | null = null, first = true;
    for (const state of [...states].reverse()) {
      let entry = this.medallions.get(state.id);
      if (!entry) {
        const face = faces.get(state.id)!;
        entry = { medallion: new HeroMedallion(state.icon, state.tint, this.glow, typeof face === 'string' ? null : face), face, own: state.own, slot: new THREE.Vector2(NaN, NaN) };
        entry.medallion.userData.medallion = state.id; this.scene.add(entry.medallion); this.medallions.set(state.id, entry);
      }
      entry.own = state.own;
      const { medallion } = entry, scale = (state.own ? 1 : .62) * this.zoom * fit;
      Object.assign(medallion, { hp: state.hp, ward: state.ward ?? 0, actions: state.actions, slots: state.slots ?? HERO_ACTIONS, selected: state.selected, wounds: state.wounds, maxWounds: state.maxWounds, summons: state.summons?.count ?? 0, summonLimit: state.summons?.limit ?? 0, fury: state.fury?.dice ?? state.judgment?.dice ?? NO_DICE, furyLimit: state.fury?.max ?? state.judgment?.count ?? 0, diceKind: state.judgment ? 'judgment' : 'fury', raging: !!state.fury?.raging, away: !!state.away, fallen: !!state.fallen, size: scale, stances: state.stances ?? NO_STANCES });
      const height = MEDALLION * scale, box = this.localBox(medallion, String(medallion.children.length));
      // The first (lowest) medallion's rim and crystals stand the margin from the left and bottom edges; the rest line up on its centre line.
      // A selected medallion stands 8% larger; measured at that size, the chosen one meets the margin and the rest sit just inside it.
      const full = medallion.size * 1.06;
      left ??= this.inset + MARGIN * this.zoom - box.min.x * full;
      if (first) { y -= box.min.y * full + height / 2; first = false; }
      // A new medallion appears in its place; one that has a place glides to the new one (a reorder, the column resizing).
      if (Number.isNaN(entry.slot.x)) medallion.position.set(left, y + height / 2, 0);
      entry.slot.set(left, y + height / 2);
      y = entry.slot.y + height / 2 + 2 * this.zoom * fit;
      this.mark(state, medallion);
      this.placeQueue(state, box.max.x * full, this.zoom * fit);
    }
    for (const id of this.marks.keys()) if (!this.medallions.has(id)) this.marks.delete(id);
    for (const [id, row] of this.queues) if (!states.some(state => state.id === id && state.queue?.length)) { row.removeFromParent(); row.dispose(); this.queues.delete(id); }
  }

  /**
   * A portrait's texture, loaded once by address and cropped from its centre to the frame's shape whatever the file's own; it is blank
   * until the picture arrives.
   */
  private portrait(url: string) {
    let texture = this.portraits.get(url);
    if (!texture) {
      texture = this.loader.load(url, loaded => {
        const image = loaded.image as { width: number; height: number }, wide = image.width / image.height, frame = PORTRAIT_WIDTH / PORTRAIT_HEIGHT;
        if (wide > frame) loaded.repeat.set(frame / wide, 1); else loaded.repeat.set(1, wide / frame);
        loaded.offset.set((1 - loaded.repeat.x) / 2, (1 - loaded.repeat.y) / 2);
      });
      texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 4;
      this.portraits.set(url, texture);
    }
    return texture;
  }

  /** A hero's queued orders, in a row directly right of its medallion, centred on it; it moves with the medallion (`update`). */
  private placeQueue(state: MedallionState, halfWidth: number, scale: number) {
    if (!state.queue?.length) return;
    let row = this.queues.get(state.id);
    if (!row) { row = new OrderQueueRow(this.kit); this.scene.add(row); this.queues.set(state.id, row); }
    row.glyphs = state.queue; row.scale.setScalar(scale);
    row.userData.offset = halfWidth + 31 * scale;
  }

  /** The in-game time of day in minutes (0–1440), which colours and aims the key light. */
  setClock(minutes: number) {
    // 1 at noon, 0 at midnight, with a long golden shoulder at dawn and dusk.
    const day = Math.max(0, Math.cos((minutes - 720) / 1440 * Math.PI * 2) * .5 + .5), low = Math.max(0, 1 - Math.abs(day - .45) * 4);
    this.key.color.copy(KEY_NIGHT).lerp(KEY_DAY, day).lerp(KEY_LOW, low * .6);
    this.key.intensity = 2.6 + day * 2.6;
    this.sky.color.copy(SKY_NIGHT).lerp(SKY_DAY, day); this.sky.intensity = .8 + day * .8;
    this.lampStrength = 5.4 - day * 1.4;
    this.clock.minutes = minutes;
  }
  /** Closes the menu's open groups, for a fresh target. */
  resetMenu() { this.menu.collapse(); if (this.menu.visible) { this.menu.restart(); this.closing = false; } }
  /** How far the open menu reaches on screen, in CSS pixels. */
  get menuRadius() { return this.menu.radius * this.zoom; }
  setMenu(nodes: RadialNode[] | null, anchor: { x: number; y: number } | null) {
    // A closing menu folds into its last place before it disappears.
    if (nodes && anchor) { this.menu.setNodes(nodes); this.anchor = anchor; if (!this.menu.visible || this.closing) this.menu.unfold(); this.menu.visible = true; this.closing = false; }
    else if (this.menu.visible && !this.closing) { this.menu.fold(); this.closing = true; }
  }
  private closing = false;
  /** The bottom bar's buttons, on a rail at the bottom centre of the screen (null hides it). */
  setDock(buttons: DockButton[] | null) {
    this.docked = !!buttons?.length; this.dock.visible = this.docked;
    if (buttons?.length) this.dock.setButtons(buttons);
  }
  /** Raises a column of buttons over the bar button `from` (null lowers it). */
  setDockColumn(from: string | null, buttons: DockButton[]) { this.dock.setColumn(from, buttons); }
  flashDock(id: string) { if (id === 'pause') this.clock.flash(); else { this.dock.flash(id); this.corner.flash(id); this.clockRail.flash(id); } }
  /** Shows the clock (null hides it) and whether the game is paused, which stops its works. */
  /** Whether a fight is on; its start jolts the clock and throws sparks from its works. */
  setCombat(fighting: boolean) {
    if (fighting && !this.fighting && this.clock.visible) {
      this.clock.jolt();
      const size = (this.clock.userData.size as number | undefined) ?? 1;
      this.sparks.emit(this.clock.position.x, this.clock.position.y - 40 * size, { color: 0xffa040, count: 26, speed: 260, angle: Math.PI / 2, spread: 1.3 });
    }
    this.fighting = fighting;
  }
  /**
   * Notes what changed on a medallion since it was last drawn and marks it: a hit shakes it, flashes it red and strikes sparks from
   * its rim; healing lifts a few soft motes; a spent action crystal gives off a puff of blue embers and a lost wound crystal a spray
   * of red; Dying shatters the glass over the portrait.
   */
  private mark(state: MedallionState, medallion: HeroMedallion) {
    let marks = this.marks.get(state.id);
    if (!marks || marks.medallion !== medallion) {
      const crack = unpickable(new THREE.Mesh(new THREE.PlaneGeometry(PORTRAIT_WIDTH, PORTRAIT_HEIGHT), new THREE.MeshBasicMaterial({ map: this.crackTexture, transparent: true, opacity: 0, depthWrite: false })));
      crack.position.z = -1; crack.visible = !!this.crackTexture; medallion.add(crack);
      const flash = unpickable(new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glow, color: 0xff3a1c, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0 })));
      flash.scale.set(170, 230, 1); flash.position.z = 12; medallion.add(flash);
      marks = { medallion, hp: state.hp, ward: state.ward ?? 0, actions: state.actions, wounds: state.wounds, hit: 0, crack, flash, cracked: 0, home: new THREE.Vector2(medallion.position.x, medallion.position.y) };
      this.marks.set(state.id, marks);
    }
    const at = (p: THREE.Vector2) => { const k = medallion.size; return { x: medallion.position.x + p.x * k, y: medallion.position.y + p.y * k }; };
    const dying = !!state.dying || !!state.fallen;
    // The glass shatters at once, throwing pale splinters from where it was struck.
    if (dying && !marks.dying) {
      const half = HeroMedallion.half, impact = at(new THREE.Vector2((IMPACT.u * 2 - 1) * half.x, (1 - IMPACT.v * 2) * half.y));
      this.sparks.emit(impact.x, impact.y, { color: 0xdfe8ee, count: 18, speed: 170, life: .7, gravity: 260 });
    }
    marks.dying = dying;
    if (state.hp < marks.hp - 1e-3) {
      marks.hit = 1;
      const local = medallion.rimPoint(Math.random()), rim = at(local), angle = Math.atan2(local.y, local.x);
      this.sparks.emit(rim.x, rim.y, { color: 0xff7a3a, count: 14, speed: 230, angle, spread: .7 });
    } else if (state.hp > marks.hp + 1e-3) {
      const half = HeroMedallion.half;
      for (let n = 0; n < 3; n++) { const p = at(new THREE.Vector2((Math.random() * 2 - 1) * half.x * .8, (Math.random() * 2 - 1) * half.y * .8)); this.sparks.emit(p.x, p.y, { color: 0x9fd8a0, count: 3, speed: 30, life: 1.3, gravity: -45 }); }
    }
    // Crystals go out one by one: the spent action crystal is the one after the lit ones, the lost wound crystal the one after the spared.
    for (let k = state.actions; k < marks.actions; k++) {
      const p = at(medallion.actionPoint(k, Math.max(2, marks.actions)));
      this.sparks.emit(p.x, p.y, { color: 0x5a9cff, count: 8, speed: 80, life: .8, gravity: 40 });
    }
    for (let k = state.maxWounds - state.wounds; k < state.maxWounds - marks.wounds; k++) {
      const p = at(medallion.woundPoint(k, state.maxWounds));
      this.sparks.emit(p.x, p.y, { color: 0xff3a20, count: 10, speed: 150, angle: Math.PI / 2, spread: 1.1 });
    }
    // A broken ward throws gold splinters from its lower edge.
    const ward = state.ward ?? 0;
    if (ward < marks.ward - 1e-3) {
      const half = HeroMedallion.half, y = half.y - Math.min(1, marks.ward) * half.y * 2;
      for (let n = 0; n < 3; n++) { const p = at(new THREE.Vector2((Math.random() * 2 - 1) * half.x * .8, y)); this.sparks.emit(p.x, p.y, { color: 0xf0d590, count: 5, speed: 120, life: .6, gravity: 220 }); }
    }
    marks.ward = ward; marks.hp = state.hp; marks.actions = state.actions; marks.wounds = state.wounds;
  }
  setPaused(paused: boolean | null) { this.clock.visible = paused !== null; this.clock.paused = !!paused; }
  /** The buttons on the rail beside the clock (null or none hides it). */
  setClockRail(buttons: DockButton[] | null) {
    this.clockRail.visible = !!buttons?.length;
    if (buttons?.length) this.clockRail.setButtons(buttons);
  }
  /** How far the clock's left outline stands from the right edge of the screen, in CSS pixels: panels opened from it stand beside it. */
  get clockSpan() { return this.size.width - this.clockLeft; }
  private clockLeft = 0;
  /** The top right corner's buttons (null or none hides the rail). */
  setCorner(buttons: DockButton[] | null) {
    this.corner.visible = !!buttons?.length;
    if (buttons?.length) this.corner.setButtons(buttons);
  }
  /** The status lines at the top centre: the combat status and Paused (null lifts either away). */
  setBanners(combat: string | null, paused: string | null) { this.combatBanner.setText(combat); this.pausedBanner.setText(paused); }
  /** Shows the map chart (null hides it). */
  setChart(state: ChartState | null) {
    this.chart.visible = !!state;
    if (!state) return;
    this.chart.setState(state);
    if (state.expanded !== this.chartOpen) { this.chartOpen = state.expanded; this.chart.jolt(); }
  }
  /** How many CSS pixels the chart's drawing spans on screen, edge to edge, as it stands now. */
  chartSpan() { return octagonVertex(CHART_FACE) * 2 * this.chart.scale.x; }
  flashChart(id: string) { (id === 'chart:follow' ? this.chart.follow : this.chart.expand).flash(); }
  /** Marks a chosen entry with a flash and a gilt pulse. */
  confirm(id: string) { this.menu.confirm(id); }
  /** Opens or closes a group in the menu; false for an entry that is an ability. */
  chooseGroup(id: string) { return this.menu.choose(id); }
  menuNode(id: string) { return this.menu.node(id); }

  /** What is under a point in CSS pixels from the canvas's top left; `menuOnly` looks at the menu alone (drawn over a dialog). */
  pick(point: { x: number; y: number }, menuOnly = false): HudHit | null {
    this.raycaster.setFromCamera(POINT.set(point.x / this.size.width * 2 - 1, -(point.y / this.size.height) * 2 + 1), this.camera);
    const socket = this.menu.visible ? this.menu.pick(this.raycaster) : null;
    if (socket) return { kind: 'socket', id: socket };
    if (menuOnly) return null;
    if (this.chart.visible) {
      const button = this.chart.pickButton(this.raycaster);
      if (button) return { kind: 'chart', id: button };
      const spot = this.chart.pickFace(this.raycaster);
      if (spot) return { kind: 'chart', id: 'face', ...spot };
      // The rim, plaque and rose are solid: a click on them is the chart's, not the world's.
      if (this.chart.covers(this.raycaster)) return { kind: 'chart', id: 'frame' };
    }
    if (this.clock.visible && this.raycaster.intersectObject(this.clock, true).length) return { kind: 'dock', id: 'pause' };
    const beside = this.clockRail.visible ? this.clockRail.pick(this.raycaster) : null;
    if (beside) return { kind: 'dock', id: beside };
    const corner = this.corner.visible ? this.corner.pick(this.raycaster) : null;
    if (corner) return { kind: 'dock', id: corner };
    const button = this.dock.visible ? this.dock.pick(this.raycaster) : null;
    if (button) return { kind: 'dock', id: button };
    for (const hit of this.raycaster.intersectObjects([...this.medallions.values()].map(entry => entry.medallion), true)) {
      // A stance notch is its own target while it shows; a sunk one is not there to click.
      let stance: MedallionStance | undefined, sunk = false;
      for (let object: THREE.Object3D | null = hit.object; object; object = object.parent) {
        if (object.userData.stanceNotch) { if (object.visible) stance = object.userData.stance as MedallionStance; else sunk = true; }
        if (object.userData.medallion && !sunk) return stance ? { kind: 'stance', id: object.userData.medallion as string, stance } : { kind: 'medallion', id: object.userData.medallion as string };
      }
    }
    return null;
  }
  /** World hover follows the same picking as unit outlines and clears when the pointer leaves the world. */
  hoverUnit(id: string | null) { this.worldHovered = id; }
  /** A HUD control takes precedence over any unit behind it. */
  get hoveredHeroId(): string | null {
    const id = this.hovered ? this.hovered.kind === 'medallion' || this.hovered.kind === 'stance' ? this.hovered.id : null : this.worldHovered;
    return id && this.medallions.has(id) ? id : null;
  }
  hover(hit: HudHit | null) {
    this.hovered = hit;
    this.menu.hover(hit?.kind === 'socket' ? hit.id : null);
    this.dock.hover(hit?.kind === 'dock' ? hit.id : null); this.corner.hover(hit?.kind === 'dock' ? hit.id : null); this.clockRail.hover(hit?.kind === 'dock' ? hit.id : null);
    this.clock.hovered = hit?.kind === 'dock' && hit.id === 'pause';
    this.chart.follow.hovered = hit?.kind === 'chart' && hit.id === 'chart:follow'; this.chart.expand.hovered = hit?.kind === 'chart' && hit.id === 'chart:expand';
  }

  update(dt: number) {
    this.time += dt;
    const flicker = 1 + Math.sin(this.time * 11.3) * .04 + Math.sin(this.time * 5.7) * .05;
    // The lamp hangs to the left of the column, low over it, halfway up, so it grazes the medallions from the side.
    let first: HeroMedallion | undefined, height = 0;
    for (const { medallion } of this.medallions.values()) { first = medallion; height += medallion.position.y; }
    if (first) this.lamp.position.set(first.position.x - 150, height / this.medallions.size + 60, 70);
    this.lamp.intensity = this.lampStrength * flicker;
    const hovered = this.hoveredHeroId;
    for (const [id, { medallion }] of this.medallions) { medallion.hovered = id === hovered; medallion.update(dt, this.time); }
    // Medallions glide to their places in the column; a dragged one follows the pointer, lifted a little out of it and in front.
    for (const [id, { slot }] of this.medallions) {
      const marks = this.marks.get(id), drag = this.dragged?.id === id;
      if (!marks) continue;
      const toX = drag ? slot.x + 18 * this.zoom : slot.x, toY = drag ? this.size.height - this.dragged!.y : slot.y, ease = 1 - Math.exp(-(drag ? 30 : 14) * dt);
      marks.home.x += (toX - marks.home.x) * ease; marks.home.y += (toY - marks.home.y) * ease;
      marks.medallion.position.z = drag ? 30 : 0;
    }
    // A struck medallion shudders and flashes, and settles; a Dying one's glass shatters.
    for (const marks of this.marks.values()) {
      marks.hit = Math.max(0, marks.hit - dt * 2.2);
      const shake = marks.hit ** 2 * 6 * this.zoom;
      marks.medallion.position.x = marks.home.x + Math.sin(this.time * 71) * shake; marks.medallion.position.y = marks.home.y + Math.cos(this.time * 53) * shake * .6;
      marks.flash.material.opacity = marks.hit * .55;
      marks.cracked += ((marks.dying ? 1 : 0) - marks.cracked) * Math.min(1, dt * (marks.dying ? 22 : 3));
      marks.crack.material.opacity = marks.cracked;
    }
    for (const [id, row] of this.queues) {
      const medallion = this.medallions.get(id)?.medallion;
      if (medallion) row.position.set(medallion.position.x + (row.userData.offset ?? 0), medallion.position.y, medallion.position.z + 40);
    }
    this.sparks.zoom = this.zoom; this.sparks.update(dt);
    if (this.anchor) {
      // In front of the unfolded chart, so a ping wheel opened on it stands over it.
      const lift = this.chart.visible ? 120 * this.unfold : 0;
      this.menu.position.set(this.anchor.x, this.size.height - this.anchor.y, lift); this.menu.scale.setScalar(this.zoom);
      this.menuLamp.position.set(this.menu.position.x - 150, this.menu.position.y + 110, 80 + lift);
    }
    if (this.closing && this.menu.folded) { this.menu.visible = false; this.closing = false; this.menu.collapse(); }
    this.menuLamp.intensity = this.menu.visible ? this.lampStrength * .85 * flicker : 0;
    this.menu.update(dt);
    if (this.docked) {
      // The rail's buttons (not a raised weapon column) stand the margin above the bottom edge.
      const rail = this.localBox(this.dock, String(this.dock.length));
      this.dock.position.set(this.size.width / 2, MARGIN * this.zoom - rail.min.y * this.zoom * DOCK_SIZE, 0); this.dock.scale.setScalar(this.zoom * DOCK_SIZE);
      // Its own lamp, above and to the left, rakes along the rail.
      this.dockLamp.position.set(this.dock.position.x - 220 * this.zoom, this.dock.position.y + 120 * this.zoom, 90);
    }
    this.dockLamp.intensity = this.dock.visible ? this.lampStrength * .7 * flicker : 0;
    this.dock.update(dt, this.time);
    // The clock stands in the bottom right corner, its own lamp above and to its left.
    const size = this.zoom * 2.25, works = this.localBox(this.clock);
    this.clock.userData.size = size; this.clock.position.set(this.size.width - MARGIN * this.zoom - works.max.x * size, MARGIN * this.zoom - works.min.y * size, this.clock.position.z);
    this.clockLamp.position.set(this.clock.position.x - 95 * size, this.clock.position.y + 85 * size, 60 * size);
    this.clockLamp.intensity = this.clock.visible ? this.lampStrength * .75 * flicker : 0;
    this.clock.update(dt, this.time);
    // Its rail stands just left of its works, its foot on the bottom margin.
    this.clockLeft = this.clock.position.x + works.min.x * size;
    const railSize = this.zoom * 1.05, rail = this.localBox(this.clockRail, String(this.clockRail.length));
    this.clockRail.scale.setScalar(railSize);
    this.clockRail.position.set(this.clockLeft - 8 * this.zoom - rail.max.x * railSize, MARGIN * this.zoom - rail.min.y * railSize, 0);
    this.clockRail.update(dt, this.time);
    // The chart hangs at the top right; unfolded, it stands large in the middle of the screen, in front of everything else.
    this.unfold += ((this.chartOpen ? 1 : 0) - this.unfold) * Math.min(1, dt * 9);
    const small = this.zoom, large = Math.max(small, Math.min(this.size.width, this.size.height) * .46 / CHART_REACH), t = this.unfold * this.unfold * (3 - 2 * this.unfold);
    // At rest its rose's tips stand the margin from the right edge, and a margin's gap below the corner rail (or the top edge).
    const rose = this.localBox(this.chart), below = this.corner.visible ? this.cornerBottom : this.size.height - MARGIN * this.zoom;
    const corner = { x: this.size.width - MARGIN * small - rose.max.x * small, y: below - (this.corner.visible ? MARGIN * .5 * small : 0) - rose.max.y * small };
    this.chart.scale.setScalar(small + (large - small) * t);
    this.chart.position.set(corner.x + (this.size.width / 2 - corner.x) * t, corner.y + (this.size.height / 2 - corner.y) * t, 60 * t);
    this.chartLamp.position.set(this.chart.position.x - 160 * this.chart.scale.x, this.chart.position.y + 120 * this.chart.scale.x, 90 + 60 * t);
    this.chartLamp.intensity = this.chart.visible ? this.lampStrength * .75 * flicker : 0;
    this.chart.update(dt);
    // The corner rail stands in the top right, right-aligned with the chart under it; the banners hang at the top centre.
    const cornerSize = this.zoom * .8, bar = this.localBox(this.corner, String(this.corner.length));
    this.corner.scale.setScalar(cornerSize);
    this.corner.position.set(this.size.width - MARGIN * this.zoom - bar.max.x * cornerSize, this.size.height - MARGIN * this.zoom - bar.max.y * cornerSize, 0);
    this.cornerBottom = this.corner.position.y + bar.min.y * cornerSize;
    this.cornerLamp.position.set(this.corner.position.x - 120 * this.zoom, this.corner.position.y + 60 * this.zoom, 80);
    this.cornerLamp.intensity = this.corner.visible ? this.lampStrength * .6 * flicker : 0;
    this.corner.update(dt, this.time);
    // The plaques hang the margin below the top edge, Paused under the combat line when both show.
    let top = this.size.height - MARGIN * this.zoom;
    for (const [banner, scale] of [[this.combatBanner, 1], [this.pausedBanner, 1]] as const) {
      const plate = this.localBox(banner), size = this.zoom * scale;
      banner.position.set(this.size.width / 2, top - plate.max.y * size, 0); banner.scale.setScalar(size);
      banner.update(dt);
      if (banner.visible) top = banner.position.y + plate.min.y * size - MARGIN * .4 * this.zoom;
    }
  }

  /**
   * The screen boxes (CSS pixels from the canvas's top left) the shown pieces cover, from their measured outlines and where they
   * stand now: cheap enough to ask every frame.
   */
  blocks() {
    const blocks: { left: number; right: number; top: number; bottom: number }[] = [];
    const add = (piece: THREE.Object3D, box: THREE.Box3 | undefined, scale = piece.scale.x) => {
      if (!piece.visible || !box) return;
      blocks.push({ left: piece.position.x + box.min.x * scale, right: piece.position.x + box.max.x * scale,
        top: this.size.height - (piece.position.y + box.max.y * scale), bottom: this.size.height - (piece.position.y + box.min.y * scale) });
    };
    for (const piece of [this.clock, this.clockRail, this.chart, this.dock, this.corner, this.combatBanner, this.pausedBanner]) add(piece, this.boxes.get(piece)?.box);
    for (const { medallion } of this.medallions.values()) add(medallion, this.boxes.get(medallion)?.box);
    return blocks;
  }

  /** A piece's solid box in its own frame, measured once per `key`. */
  private localBox(piece: THREE.Object3D, key = '') {
    const cached = this.boxes.get(piece);
    if (cached?.key === key) return cached.box;
    piece.updateMatrixWorld(true);
    const box = solidBounds([piece], piece.matrixWorld.clone().invert()) ?? new THREE.Box3(new THREE.Vector3(), new THREE.Vector3());
    this.boxes.set(piece, { key, box });
    return box;
  }

  /**
   * Where each visible piece stands on screen, in CSS pixels from the canvas's top left, by its solid parts (not glows or sparks),
   * and its gap to each screen edge. For scripts and checks of the layout.
   */
  bounds() {
    const pieces: [string, THREE.Object3D][] = [['party', this.scene], ['dock', this.dock], ['clock', this.clock], ['chart', this.chart], ['corner', this.corner], ['combat', this.combatBanner], ['paused', this.pausedBanner]];
    const result: Record<string, { left: number; right: number; top: number; bottom: number }> = {};
    for (const [name, root] of pieces) {
      const box = name === 'party' ? (this.medallions.size ? solidBounds([...this.medallions.values()].map(entry => entry.medallion)) : null) : root.visible ? solidBounds([root]) : null;
      if (!box) continue;
      result[name] = { left: box.min.x, right: this.size.width - box.max.x, top: this.size.height - box.max.y, bottom: box.min.y };
    }
    return result;
  }

  /**
   * Links every shader program the layer draws with and uploads its buffers, before the first interactive frame. The layer is a scene of
   * its own, which the world's warm-up never reaches, so its programs otherwise link on the first frame it draws (a dozen at once) and
   * the menu's on its first opening. The pieces that exist only once data arrives (a medallion, a hero's queued orders, the rail's
   * buttons and column, the menu's sockets, arcs and confirming ring) are stood in by one of each under the layer's own lights, wearing
   * the same blank maps, so the programs are the ones play uses; `icon` is any glyph file. The stand-ins are disposed afterward and
   * `retainShaderPrograms` keeps their programs.
   */
  async warm(renderer: THREE.WebGLRenderer, icon: string, active = () => true) {
    if (!canDraw()) return;
    const stage = new THREE.Group(); stage.name = 'hud-warmup';
    const pieces: { dispose(): void }[] = [];
    const medallion = (portrait: THREE.Texture | null, id: string) => {
      const piece = new HeroMedallion(icon, 0xd8c08a, this.glow, portrait);
      this.mark({ id, icon, tint: 0xd8c08a, hp: 1, actions: 2, wounds: 0, maxWounds: 3, selected: true, own: true }, piece); this.marks.delete(id);
      // Its stance notches stand out too, so their glyph plates and glows are drawn.
      piece.stances = { move: 'run', anticipating: true, hidden: false }; piece.update(1, 0);
      stage.add(piece); pieces.push(piece);
    };
    // A portrait medallion and a glyph one: the face wears a different map set in each.
    medallion(blankFace().map, 'warm-portrait'); medallion(null, 'warm-glyph');
    const queue = new OrderQueueRow(this.kit); queue.glyphs = [icon, icon]; stage.add(queue); pieces.push(queue);
    const rail = new HudDock(this.glow, this.kit);
    rail.setButtons([{ id: 'a', icon }, { id: 'b', icon, pressed: true, apart: true }, { id: 'c', icon, disabled: true, large: true }]);
    rail.setColumn('a', [{ id: 'x', icon }, { id: 'y', icon, pressed: true }]);
    stage.add(rail); pieces.push(rail);
    const menu = new RadialMenu([{ id: 'one', icon, tint: 0xd8c08a, autocast: true }, { id: 'two', icon, tint: 0xd8c08a, disabled: true },
      { id: 'group', icon, tint: 0xd8c08a, children: [{ id: 'child', icon, tint: 0xd8c08a, armed: true, autocast: false }] }], this.glow, this.kit);
    menu.choose('group'); menu.confirm('one'); stage.add(menu); pieces.push(menu);
    this.scene.add(stage);
    this.chart.warm(renderer);
    try { await warmScene(renderer, this.scene, this.camera, active); }
    finally { stage.removeFromParent(); for (const piece of pieces) piece.dispose(); }
  }

  /**
   * Draws over whatever the renderer drew last, keeping its colour and clearing only depth. `part` leaves pieces out of this draw: the
   * menu alone (`menu`, lifted over a dialog) or everything but it (`rest`, under the dialog). The lights stay in both.
   */
  render(renderer: THREE.WebGLRenderer, width: number, height: number, part: 'all' | 'menu' | 'rest' = 'all') {
    if (width !== this.size.width || height !== this.size.height) {
      this.size = { width, height };
      Object.assign(this.camera, { left: 0, right: width, top: height, bottom: 0 }); this.camera.updateProjectionMatrix();
    }
    const left = this.left; left.length = 0;
    if (part !== 'all') for (const child of this.scene.children) if (child.visible && !(child instanceof THREE.Light) && (child === this.menu) === (part === 'rest')) { child.visible = false; left.push(child); }
    const autoClear = renderer.autoClear, shadows = renderer.shadowMap.autoUpdate;
    renderer.autoClear = false; renderer.shadowMap.autoUpdate = false;
    if (this.chart.visible && renderer !== this.chartRenderer) { this.chartRenderer = renderer; this.chart.invalidate(); }
    this.chart.draw(renderer);
    // Glows, flashes and cracks faded to nothing still cost a draw call each: hide them for the draw (picking never sees them).
    const faint = this.faint; faint.length = 0;
    this.scene.traverseVisible(object => {
      const material = (object as THREE.Mesh).material;
      if (material && !Array.isArray(material) && material.transparent && material.opacity < FAINT && !object.children.length) { object.visible = false; faint.push(object); }
    });
    renderer.clearDepth(); renderer.render(this.scene, this.camera);
    for (const object of faint) object.visible = true;
    for (const object of left) object.visible = true;
    renderer.autoClear = autoClear; renderer.shadowMap.autoUpdate = shadows;
  }
  private left: THREE.Object3D[] = [];
  /** The renderer the chart's drawing was last decoded in. */
  private chartRenderer: THREE.WebGLRenderer | null = null;

  dispose() {
    for (const { medallion } of this.medallions.values()) medallion.dispose();
    for (const row of this.queues.values()) row.dispose();
    this.menu.dispose(); this.dock.dispose(); this.clock.dispose(); this.chart.dispose(); this.corner.dispose(); this.clockRail.dispose(); this.sparks.dispose(); this.crackTexture?.dispose(); this.kit.dispose();
    for (const marks of this.marks.values()) { marks.crack.material.dispose(); marks.flash.material.dispose(); } this.combatBanner.dispose(); this.pausedBanner.dispose(); for (const texture of this.portraits.values()) texture.dispose(); this.glow?.dispose();
  }
}

/** What a medallion last showed, and the marks a change leaves on it. */
interface MedallionMarks {
  medallion: HeroMedallion; hp: number; ward: number; actions: number; wounds: number;
  /** How hard it was just struck (1, fading to 0). */
  hit: number;
  dying?: boolean;
  crack: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  flash: THREE.Sprite;
  /** How far the cracks have spread (0–1). */
  cracked: number;
  /** Where it stands as it glides to its place in the column, which the shudder plays around. */
  home: THREE.Vector2;
}

/** Where the glass was struck, as a share of the portrait's width and height from its top left. */
const IMPACT = { u: .44, v: .34 };

/**
 * The glass over a portrait, shattered: a spiderweb run out from one impact, radial cracks to the frame crossed by rings that sag
 * between them, each shard catching the light a little differently, every crack a dark line with a bright lip where the broken edge
 * takes the light, and the impact itself crushed white. Drawn at the portrait's own shape so nothing is stretched.
 */
function crackTexture() {
  const width = 256, height = Math.round(width * PORTRAIT_HEIGHT / PORTRAIT_WIDTH), canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const g = canvas.getContext('2d')!, cx = width * IMPACT.u, cy = height * IMPACT.v, reach = Math.hypot(width, height);
  let seed = 11; const random = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  // Radial cracks, unevenly spaced, and rings at growing radii; each crossing is jittered so no shard is regular.
  const spokes = Array.from({ length: 13 }, (_, i) => (i + (random() - .5) * .7) / 13 * Math.PI * 2);
  const rings = [9, 20, 36, 58, 88, 128, reach];
  const point = (i: number, k: number) => {
    const angle = spokes[(i + spokes.length) % spokes.length], r = rings[k] * (k ? .85 + random() * .3 : 1);
    return { x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r };
  };
  const web = rings.map((_, k) => spokes.map((_, i) => point(i, k)));
  // The shards: each a faint pane of its own brightness, darker or lighter as its plane tilts against the light.
  for (let k = 0; k + 1 < rings.length; k++) for (let i = 0; i < spokes.length; i++) {
    const j = (i + 1) % spokes.length, tilt = random();
    g.fillStyle = tilt > .62 ? `rgba(235,240,245,${.05 + (tilt - .62) * .3})` : `rgba(0,0,0,${tilt * .14})`;
    g.beginPath(); g.moveTo(web[k][i].x, web[k][i].y); g.lineTo(web[k][j].x, web[k][j].y); g.lineTo(web[k + 1][j].x, web[k + 1][j].y); g.lineTo(web[k + 1][i].x, web[k + 1][i].y); g.closePath(); g.fill();
  }
  g.lineCap = 'round'; g.lineJoin = 'round';
  // A crack: a dark line, with a bright lip a pixel to one side where the broken edge catches the light.
  const crack = (points: { x: number; y: number }[], width: number) => {
    for (const [color, offset, scale] of [['rgba(255,255,255,.75)', -.9, .55], ['rgba(10,12,14,.9)', 0, 1]] as const) {
      g.strokeStyle = color; g.lineWidth = width * scale; g.beginPath();
      points.forEach((p, n) => n ? g.lineTo(p.x + offset, p.y + offset) : g.moveTo(p.x + offset, p.y + offset)); g.stroke();
    }
  };
  // Radials run the whole way out, kinking a little at each ring; rings sag between spokes and break off now and then.
  for (let i = 0; i < spokes.length; i++) crack(web.map(ring => ring[i]), 1.7);
  for (let k = 1; k + 1 < rings.length; k++) for (let i = 0; i < spokes.length; i++) {
    if (random() < .18 + k * .06) continue;
    const a = web[k][i], b = web[k][(i + 1) % spokes.length], sag = .82 + random() * .1;
    crack([a, { x: cx + ((a.x + b.x) / 2 - cx) * sag, y: cy + ((a.y + b.y) / 2 - cy) * sag }, b], 1.2);
  }
  // The impact: glass crushed to a white powder of tiny splinters.
  const crush = g.createRadialGradient(cx, cy, 0, cx, cy, 14);
  crush.addColorStop(0, 'rgba(245,248,250,.85)'); crush.addColorStop(.5, 'rgba(225,232,238,.35)'); crush.addColorStop(1, 'rgba(225,232,238,0)');
  g.fillStyle = crush; g.beginPath(); g.arc(cx, cy, 14, 0, Math.PI * 2); g.fill();
  for (let n = 0; n < 40; n++) {
    const a = random() * Math.PI * 2, r = random() * 12, l = 2 + random() * 5;
    crack([{ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r }, { x: cx + Math.cos(a) * (r + l), y: cy + Math.sin(a) * (r + l) }], .8);
  }
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; texture.anisotropy = 4;
  return texture;
}

/**
 * The box of the solid parts (meshes that take the pointer, not glows, sprites or sparks) of some pieces, on screen or, given
 * `frame`, in that frame. A piece's own visibility is ignored, so a hidden piece can be measured before it shows; hidden parts are not.
 */
function solidBounds(roots: THREE.Object3D[], frame?: THREE.Matrix4) {
  const box = new THREE.Box3(), matrix = new THREE.Matrix4(), point = new THREE.Vector3();
  const walk = (object: THREE.Object3D) => {
    if (object.userData.unmeasured) return;
    // Vertex by vertex, so a turning cog measures by its teeth rather than a box round them.
    if (object instanceof THREE.Mesh && object.raycast === THREE.Mesh.prototype.raycast) {
      const positions = object.geometry.getAttribute('position'), to = frame ? matrix.multiplyMatrices(frame, object.matrixWorld) : object.matrixWorld;
      for (let i = 0; i < (positions?.count ?? 0); i++) box.expandByPoint(point.fromBufferAttribute(positions, i).applyMatrix4(to));
    }
    for (const child of object.children) if (child.visible) walk(child);
  };
  for (const root of roots) { root.updateMatrixWorld(true); walk(root); }
  return box.isEmpty() ? null : box;
}
